import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from 'components/ui/Button';
import styles from './Pagination.module.scss';

interface IProps {
  page: number;
  pages: number;
  onSelectPage: (page: number) => void;
}

// get the pattern of pages to actually show, given current page and the total number of pages
// zero or negative means showing "..."
// with the current design and this pattern pagination component should always fit into one row,
// with at least 375px screen width
const getVisiblePages = (page: number, pages: number) => {
  if (pages <= 5) {
    // small number of pages: 1, 2, [3], 4, 5 (just show all)
    return Array.from({ length: pages }).map((_, i) => i + 1);
  } else if (page <= 3) {
    // one of the first pages: 1, [2], 3, ..., 10 (show from first page to one page after the selected one, and then the last one) 
    return [...Array.from({ length: page + 1 }).map((_, i) => i + 1), 0, pages];
  } else if (pages - page <= 3) {
    // one of the last pages: 1, ..., 8, [9], 10 (show first page, and then from one page before the selected one to the last one)
    return [1, 0, ...Array.from({ length: pages - page + 2 }).map((_, i) => page - 1 + i)]
  } else {
    // page somewhere in the middle: 1, ..., 4, [5], 6, ..., 10 (show first, previous, current, next and last page)
    return [1, -1, page - 1, page, page + 1, -2, pages];
  }
};

const PageLink = ({ page, isSelected, onClick }: { page: number, isSelected: boolean, onClick: () => void }) => {
  if (page <= 0) {
    return <span>...</span>;
  }
  return <Button variant={isSelected ? "selected" : "secondary"} size="small" onClick={onClick}>{page}</Button>;
};

export const Pagination = ({ page, pages, onSelectPage }: IProps) => {
  if (pages <= 1) return null;
  return (
    <div className={styles.pagination}>
      <Button variant="secondary" size="small" state={page <= 1 ? 'disabled' : 'normal'}
              onClick={() => onSelectPage(page - 1)}>
        <ChevronLeft />
      </Button>
      {getVisiblePages(page, pages).map(n => <PageLink
        key={n}
        page={n}
        isSelected={page === n}
        onClick={() => onSelectPage(n)} 
      />)}
      <Button variant="secondary" size="small" state={page >= pages ? 'disabled' : 'normal'}
              onClick={() => onSelectPage(page + 1)}>
        <ChevronRight />
      </Button>
    </div>
  )
};